import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Mail, Lock } from 'lucide-react';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { authAPI } = await import('../services/api');
      const data = await authAPI.login(formData);
      
      // Store token and user
      localStorage.setItem('token', data.access_token);
      onLogin(data.user);
      
      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login failed",
        description: error.response?.data?.detail || "Invalid email or password.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black mb-4 text-zinc-100">LOGIN</h1>
          <p className="text-zinc-400">Access your account and live chat support</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-800 hover:bg-red-700 text-white py-6 text-lg font-bold tracking-wide transition-all duration-300"
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-red-800 hover:text-red-700 font-bold">
                Register here
              </Link>
            </p>
          </div>

          <div className="mt-6 p-4 bg-zinc-950 border border-zinc-700">
            <p className="text-zinc-500 text-sm text-center">
              Demo credentials:<br />
              <span className="text-zinc-400">Email: client@example.com</span><br />
              <span className="text-zinc-400">Password: password123</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;